/* ––
 * ––– Imports
 * –––––––––––––––––––––––––––––––– */
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import ThemeProvider from '@mui/system/ThemeProvider';
import { createTheme, responsiveFontSizes } from '@mui/material/styles';

/* ––
 * ––– Constants
 * –––––––––––––––––––––––––––––––– */
const defaultTheme = responsiveFontSizes(createTheme({
  palette: {
    type:       'light',
    background: {
      paper:     '#FFFFFF',
      dark:      '#F8F9FA',
      light:     '#D8D8D8',
      highlight: '#F2EAFF',
    },
    primary: {
      main:         '#5BC668',
      contrastText: '#192C39',
      light:        '#E8F3EB',
    },
    secondary: {
      main:         '#192C39',
      contrastText: '#192C39',
      disabled:     '#6D7278',
    },
    error: {
      main:  '#f44336',
      light: '#fee8e7',
    },
    info: {
      main:  '#199CFA',
      light: '#e3f2fd',
      dark:  '#1D2C38',
    },
    warning: {
      main: '#EBA02D',
    },
    ai: {
      main:         '#E7D9FF',
      light:        '#E7D9FF',
      dark:         '#673AB7',
      contrastText: '#192C39',
    },
    text: {
      primary:   '#192C39',
      secondary: '#686E75',
      disabled:  '#D8D8D8',

      light: {
        primary:   '#192C39',
        secondary: '#686E75',
        link:      '#054160',
        disabled:  '#D8D8D8',
      },
    },
  },
  typography: {
    // htmlFontSize: 10,
    // fontSize: 14,
    h1: {
      fontSize: '2.75rem',
    },
    h2: {
      fontSize: '2.125rem',
    },
    h3: {
      fontSize: '1.5rem',
    },
    h4: {
      fontSize: '1.25rem',
    },
    h5: {
      fontSize: '1rem',
    },
    h6: {
      fontSize: '.825rem',
    },
    button: {
      textTransform: 'none',
    },
  },
}));

/* ––
 * ––– Theme builder
 * –––––––––––––––––––––––––––––––– */
function buildTheme() {
  try {
    const eosTheme = document.getElementById('eostheme');

    if (!eosTheme) return defaultTheme;

    return responsiveFontSizes(createTheme(
      JSON.parse(eosTheme.text),
    ));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('AppTheme: failed to load custom (eos) theme. Default theme will be used instead. Error details:', error);

    return defaultTheme;
  }
}

/* ––
 * ––– Component
 * –––––––––––––––––––––––––––––––– */
function AppTheme({ children }) {
  /* –– Computed values
   * –––––––––––––––––––––––––––––––– */
  const theme = useMemo(buildTheme, []);

  /* –– Render
   * –––––––––––––––––––––––––––––––– */
  return (
    <ThemeProvider theme={theme}>
      {children}
    </ThemeProvider>
  );
}

/* ––
 * ––– Props declaration
 * –––––––––––––––––––––––––––––––– */
AppTheme.propTypes = {
  children: PropTypes.node.isRequired,
};

/* ––
 * ––– Exports
 * –––––––––––––––––––––––––––––––– */
export default AppTheme;
